/**
 * Tela /catalog/suppliers/$supplierId/arts — artes do fornecedor (TH-55167).
 *
 * Consome GET /catalog/suppliers/{supplier_id}/arts (TH-55164).
 *
 * Acesso:
 *   - super_admin + catalog_operator.
 *   - RLS no backend isola por scope='supplier'; o vínculo
 *     catalog_operator→supplier é enforçado por path param + audit (o
 *     User model não tem supplier_id). Frontend só consome.
 *
 * UX: lista paginada com filtro por status (draft, pending_review, active,
 * rejected, archived). Cards mostram thumb, status colorido, tags. O motivo
 * da rejeição (`review_notes`) NÃO está exposto pelo schema SupplierArtRead
 * (apenas `status`); upgrade futuro do schema vai trazer isso.
 *
 * Deeplink-only: não há endpoint /suppliers para popular um picker.
 * Operador chega via URL direta ou link em outra tela.
 *
 * PSHWO-64518: cada card traz o mini-gráfico de 30 dias (views × usos) da
 * arte, lendo GET /custom-arts/:id/stats — é assim que o fornecedor enxerga
 * a impressão na galeria, não só o clone.
 */

import { useMemo, useState } from "react";
import { useParams } from "@tanstack/react-router";
import { RefreshCw } from "lucide-react";

import { AppLayout } from "@/components/AppLayout";
import { ArtStatsSparkline } from "@/components/ArtStatsSparkline";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { FilterPills } from "@/components/ui/FilterPills";
import { Pagination } from "@/components/ui/Pagination";
import { Skeleton } from "@/components/ui/Skeleton";
import { StatusPill, type StatusTone } from "@/components/ui/StatusPill";
import { useArtStats } from "@/hooks/useArtStats";
import { useAuth } from "@/hooks/useAuth";
import {
  useSupplierArts,
  type SupplierArtRead,
  type SupplierArtStatus,
} from "@/hooks/useSupplierArts";
import { cn } from "@/lib/utils";

const PER_PAGE = 24;

type StatusFilter = SupplierArtStatus | "all";

const STATUS_OPTIONS: { label: string; value: StatusFilter }[] = [
  { label: "Todas", value: "all" },
  { label: "Rascunho", value: "draft" },
  { label: "Em revisão", value: "pending_review" },
  { label: "Aprovadas", value: "active" },
  { label: "Rejeitadas", value: "rejected" },
  { label: "Arquivadas", value: "archived" },
];

const STATUS_TO_LABEL: Record<SupplierArtStatus, string> = {
  draft: "Rascunho",
  pending_review: "Em revisão",
  active: "Aprovada",
  rejected: "Rejeitada",
  archived: "Arquivada",
};

const STATUS_TO_TONE: Record<SupplierArtStatus, StatusTone> = {
  draft: "muted",
  pending_review: "warn",
  active: "success",
  rejected: "destructive",
  archived: "muted",
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export function SupplierArtsPage() {
  return (
    <RequireAuth>
      <SupplierArtsContent />
    </RequireAuth>
  );
}

function SupplierArtsContent() {
  const { user, isLoading: authLoading } = useAuth();
  // useParams sem strict — esta rota não é tipada estritamente porque
  // o path tem segmento dinâmico que é só consumido aqui.
  const params = useParams({ strict: false }) as {
    supplierId?: string;
  };
  const supplierId = params.supplierId ?? "";

  if (authLoading) {
    return (
      <AppLayout
        breadcrumb="CATÁLOGO / ARTES DO FORNECEDOR"
        title="Artes do fornecedor"
      >
        <div className="text-sm text-[var(--color-muted-foreground)]">
          carregando…
        </div>
      </AppLayout>
    );
  }

  if (user?.role !== "super_admin" && user?.role !== "catalog_operator") {
    return (
      <AppLayout
        breadcrumb="CATÁLOGO / ARTES DO FORNECEDOR"
        title="Artes do fornecedor"
      >
        <NotAuthorized />
      </AppLayout>
    );
  }

  if (!supplierId) {
    return (
      <AppLayout
        breadcrumb="CATÁLOGO / ARTES DO FORNECEDOR"
        title="Artes do fornecedor"
      >
        <MissingSupplier />
      </AppLayout>
    );
  }

  return <SupplierArtsDashboard supplierId={supplierId} />;
}

interface SupplierArtsDashboardProps {
  supplierId: string;
}

function SupplierArtsDashboard({ supplierId }: SupplierArtsDashboardProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1);

  const filters = useMemo(
    () => ({
      supplierId,
      status: statusFilter,
      page,
      per_page: PER_PAGE,
    }),
    [supplierId, statusFilter, page],
  );

  const query = useSupplierArts(filters);
  const rows = query.data?.data ?? [];
  const total = query.data?.meta.total ?? 0;

  const actions = (
    <Button
      variant="outline"
      size="sm"
      onClick={() => void query.refetch()}
      disabled={query.isFetching}
      aria-label="Atualizar"
    >
      <RefreshCw
        className={cn(
          "mr-2 h-4 w-4",
          query.isFetching ? "animate-spin" : null,
        )}
      />
      {query.isFetching ? "Atualizando…" : "Atualizar"}
    </Button>
  );

  return (
    <AppLayout
      breadcrumb="CATÁLOGO / ARTES DO FORNECEDOR"
      title="Artes do fornecedor"
      actions={actions}
    >
      <div className="mx-auto max-w-7xl space-y-4">
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Catálogo de artes do fornecedor{" "}
          <span className="font-mono text-[12px] text-[var(--color-foreground)]">
            {supplierId.slice(0, 8)}
          </span>
          .{" "}
          {total > 0 ? (
            <span className="ml-1 font-mono tabular-nums text-[var(--color-foreground)]">
              {total} {total === 1 ? "arte" : "artes"}
            </span>
          ) : null}
        </p>

        <div role="tablist" aria-label="Filtrar por status">
          <FilterPills
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
          />
        </div>

        {query.error ? (
          <ErrorBox
            message={query.error.message}
            onRetry={() => void query.refetch()}
          />
        ) : null}

        {query.isLoading ? (
          <GallerySkeleton />
        ) : rows.length === 0 ? (
          <EmptyState statusFilter={statusFilter} />
        ) : (
          <ul
            role="list"
            aria-label="Artes do fornecedor"
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4"
          >
            {rows.map((art) => (
              <li key={art.id}>
                <SupplierArtCard art={art} />
              </li>
            ))}
          </ul>
        )}

        {query.data && total > PER_PAGE ? (
          <div className="mt-4">
            <Pagination
              page={page}
              perPage={PER_PAGE}
              total={total}
              onPageChange={setPage}
            />
          </div>
        ) : null}
      </div>
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

function SupplierArtCard({ art }: { art: SupplierArtRead }) {
  const tone = STATUS_TO_TONE[art.status] ?? "muted";
  const label = STATUS_TO_LABEL[art.status] ?? art.status;

  return (
    <article className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-card)]">
      <div className="relative aspect-video w-full bg-[var(--color-muted-tint)]">
        <img
          src={art.art_url}
          alt={`Arte: ${art.name}`}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute right-2 top-2">
          <StatusPill tone={tone}>{label}</StatusPill>
        </div>
      </div>
      <div className="space-y-1.5 p-3">
        <h3
          className="line-clamp-2 text-sm font-medium leading-tight"
          title={art.name}
        >
          {art.name}
        </h3>
        <p className="font-mono text-[11px] text-[var(--color-muted-foreground)]">
          Criada em {formatDate(art.created_at)}
        </p>
        {art.tags && art.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1 pt-1">
            {art.tags.slice(0, 4).map((t) => (
              <span
                key={t}
                className="rounded-full border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-muted-foreground)]"
              >
                {t}
              </span>
            ))}
          </div>
        ) : null}
        <ArtStatsChart artId={art.id} artName={art.name} />
      </div>
    </article>
  );
}

/**
 * PSHWO-64518: mini-gráfico 30d (views × usos) da arte.
 *
 * Lê GET /custom-arts/{id}/stats. Sem dado (erro/403) o bloco some — a
 * telemetria é complemento do catálogo, não pode quebrar a listagem.
 */
function ArtStatsChart({
  artId,
  artName,
}: {
  artId: string;
  artName: string;
}) {
  const stats = useArtStats(artId);

  if (stats.isLoading) {
    return <Skeleton className="mt-1 h-10 w-full" />;
  }
  if (!stats.data) {
    return null;
  }

  return (
    <div className="border-t border-[var(--color-border)] pt-2">
      <ArtStatsSparkline points={stats.data.daily_30d} label={artName} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Auxiliares: error, empty, no-supplier, no-auth
// ---------------------------------------------------------------------------

function ErrorBox({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div
      role="alert"
      className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]"
    >
      Erro ao carregar artes: {message}.{" "}
      <button type="button" onClick={onRetry} className="underline">
        Tentar novamente
      </button>
    </div>
  );
}

function EmptyState({ statusFilter }: { statusFilter: StatusFilter }) {
  const message =
    statusFilter === "all"
      ? "Este fornecedor ainda não tem artes."
      : "Nenhuma arte bate com o filtro de status.";
  return (
    <div className="rounded-md border border-dashed border-[var(--color-border-strong)] p-8 text-center">
      <p className="text-sm text-[var(--color-muted-foreground)]">{message}</p>
    </div>
  );
}

function GallerySkeleton() {
  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4"
      aria-busy="true"
      aria-label="Carregando artes…"
    >
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-card)]"
        >
          <Skeleton className="aspect-video w-full rounded-none" />
          <div className="space-y-2 p-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

function MissingSupplier() {
  return (
    <div className="mx-auto max-w-xl rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-8 text-center">
      <div className="text-base font-semibold">Fornecedor não informado</div>
      <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
        Acesse esta tela com a URL{" "}
        <code className="font-mono">/catalog/suppliers/&lt;ID&gt;/arts</code>.
      </p>
    </div>
  );
}

function NotAuthorized() {
  return (
    <div className="mx-auto max-w-xl rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-8 text-center">
      <div className="text-base font-semibold">Acesso restrito</div>
      <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
        Esta página é exclusiva para super_admin e catalog_operator.
      </p>
    </div>
  );
}
